import { useState, useEffect } from "react";
import { Box, Typography, CircularProgress, Tooltip } from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import dayjs from "dayjs";
import strings from "../../i18n/language";
import { getVersionsAction } from "../../redux/actions/version";

const BRAND = '#3b95e8';
const GLASS = 'rgba(255,255,255,0.04)';
const BORDER = 'rgba(255,255,255,0.08)';
const MUTED = 'rgba(232,240,254,0.38)';
const TEXT = '#e8f0fe';
const GOOD = '#5bbfb5';
const GOLD = '#ffc107';
const BAD = '#e05c5c';

const WINDOWS = [7, 30];
const PLATFORM_LABELS = { android: 'Android', ios: 'iOS' };
const TYPE_LABELS = { apple: 'Apple', google: 'Google', local: 'Local', web: 'Web' };

const card = {
  background: GLASS,
  border: `1px solid ${BORDER}`,
  borderRadius: 20,
  backdropFilter: 'blur(12px)',
  overflow: 'hidden',
};

const FilterChip = ({ label, active, onClick }) => (
  <button onClick={onClick} style={{
    background: active ? 'rgba(59,149,232,0.15)' : 'rgba(255,255,255,0.04)',
    border: `1px solid ${active ? 'rgba(59,149,232,0.5)' : BORDER}`,
    borderRadius: 8,
    color: active ? BRAND : MUTED,
    fontSize: 12,
    fontWeight: active ? 600 : 400,
    padding: '5px 12px',
    cursor: 'pointer',
    transition: 'all 0.15s',
    letterSpacing: 0.2,
  }}>
    {label}
  </button>
);

const ColHeader = ({ children, style }) => (
  <th style={{
    textAlign: 'left',
    fontSize: 11,
    fontWeight: 600,
    color: MUTED,
    textTransform: 'uppercase',
    letterSpacing: 0.6,
    padding: '14px 16px',
    borderBottom: `1px solid ${BORDER}`,
    ...style,
  }}>
    {children}
  </th>
);

const Td = ({ children, style }) => (
  <td style={{
    padding: '13px 16px',
    fontSize: 13,
    color: 'rgba(232,240,254,0.75)',
    borderBottom: `1px solid rgba(255,255,255,0.04)`,
    verticalAlign: 'middle',
    ...style,
  }}>
    {children}
  </td>
);

const SectionTitle = ({ children }) => (
  <div style={{ padding: '18px 24px 6px', fontSize: 13, fontWeight: 700, color: TEXT }}>{children}</div>
);

// "2.0.2 (200020006)", and plain words for the buckets of apps too old to
// send their version
const versionLabel = (version, build) => {
  if (version === '<=2.0.1') return strings.LABEL_VERSIONS_UP_TO_201;
  if (version === '1.4.x') return strings.LABEL_VERSIONS_14X;
  if (version === 'older') return strings.formatString(strings.LABEL_VERSIONS_OLDER, build || '?');
  return build ? `${version} (${build})` : version;
};

const PlatformCard = ({ platform }) => {
  const color = platform.share >= 80 ? GOOD : platform.share >= 50 ? GOLD : BAD;
  return (
    <div style={{ flex: 1, minWidth: 220, padding: '18px 20px', borderRadius: 16, background: 'rgba(255,255,255,0.03)', border: `1px solid ${BORDER}` }}>
      <div style={{ fontSize: 11, fontWeight: 600, color: MUTED, textTransform: 'uppercase', letterSpacing: 0.6 }}>
        {PLATFORM_LABELS[platform.platform] || platform.platform}
      </div>
      {platform.users ? (
        <>
          <div style={{ fontSize: 36, fontWeight: 800, color, lineHeight: 1.15, marginTop: 4 }}>{platform.share}%</div>
          <div style={{ fontSize: 12, color: MUTED, marginTop: 4 }}>
            {strings.formatString(strings.LABEL_VERSIONS_ON_LATEST, platform.onLatest, platform.users, versionLabel(platform.latestVersion || '—', platform.latestBuild))}
          </div>
        </>
      ) : (
        <div style={{ fontSize: 13, color: MUTED, marginTop: 10 }}>{strings.LABEL_VERSIONS_NO_DATA}</div>
      )}
      {platform.fromStore === false && (
        <div style={{ fontSize: 11, color: GOLD, marginTop: 6 }}>{strings.LABEL_VERSIONS_STORE_UNKNOWN}</div>
      )}
    </div>
  );
};

// Google and Apple accounts can be looked up in Firebase by their id;
// device-only accounts have no contact
const Contact = ({ user }) => {
  if (user.type === 'google' || user.type === 'apple') {
    return (
      <Tooltip title={strings.LABEL_RATING_FIREBASE} placement="left">
        <span style={{ fontFamily: 'monospace', fontSize: 11, userSelect: 'all', wordBreak: 'break-all' }}>{user.id}</span>
      </Tooltip>
    );
  }
  return <span style={{ color: MUTED }}>{strings.LABEL_RATING_NO_CONTACT}</span>;
};

const VersionList = () => {
  const [days, setDays] = useState(30);
  const dispatch = useDispatch();
  const selectedCompany = useSelector((state) => state.login.selectedCompany);
  const stats = useSelector((state) => state.version.stats);
  const isLoading = useSelector((state) => state.version.isLoadingGetVersions);

  useEffect(() => {
    dispatch(getVersionsAction(days, selectedCompany?.id));
  }, [dispatch, days, selectedCompany]);

  return (
    <Box sx={{ width: "100%" }}>
      <div style={card}>

        <div style={{ padding: '20px 24px', borderBottom: `1px solid ${BORDER}` }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
            <Typography style={{ fontSize: 15, fontWeight: 700, color: TEXT, letterSpacing: 0.2 }}>
              {strings.LABEL_VERSIONS_TITLE}
            </Typography>
            {stats && (
              <span style={{ fontSize: 12, color: MUTED }}>
                {stats.total} {strings.LABEL_VERSIONS_ACTIVE_USERS}
              </span>
            )}
          </div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
            {WINDOWS.map((w) => (
              <FilterChip
                key={w}
                label={strings.formatString(strings.LABEL_VERSIONS_LAST_DAYS, w)}
                active={days === w}
                onClick={() => setDays(w)}
              />
            ))}
            <span style={{ fontSize: 12, color: MUTED, marginLeft: 8 }}>
              {selectedCompany
                ? strings.formatString(strings.LABEL_VERSIONS_SCOPE_COMPANY, selectedCompany.name)
                : strings.LABEL_VERSIONS_SCOPE_ALL}
            </span>
          </div>
        </div>

        {isLoading && (
          <div style={{ textAlign: 'center', padding: 40 }}>
            <CircularProgress size={24} sx={{ color: BRAND }} />
          </div>
        )}

        {!isLoading && stats && (
          <>
            <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', padding: '20px 24px' }}>
              {stats.platforms.map((p) => <PlatformCard key={p.platform} platform={p} />)}
            </div>
            <div style={{ padding: '0 24px 8px', fontSize: 12, color: MUTED }}>{strings.LABEL_VERSIONS_HINT}</div>

            <SectionTitle>{strings.LABEL_VERSIONS_BY_VERSION}</SectionTitle>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr>
                    <ColHeader>{strings.LABEL_VERSIONS_PLATFORM}</ColHeader>
                    <ColHeader>{strings.LABEL_VERSIONS_VERSION}</ColHeader>
                    <ColHeader>{strings.LABEL_VERSIONS_USERS}</ColHeader>
                    <ColHeader>%</ColHeader>
                    <ColHeader>{strings.LABEL_VERSIONS_LAST_SEEN}</ColHeader>
                  </tr>
                </thead>
                <tbody>
                  {stats.versions.length === 0 && (
                    <tr><td colSpan={5} style={{ textAlign: 'center', padding: 30, fontSize: 13, color: MUTED }}>{strings.LABEL_VERSIONS_NO_DATA}</td></tr>
                  )}
                  {stats.versions.map((v) => (
                    <tr key={`${v.platform}|${v.version}|${v.build}`}>
                      <Td>{PLATFORM_LABELS[v.platform] || v.platform}</Td>
                      <Td style={{ color: TEXT, fontWeight: 600 }}>{versionLabel(v.version, v.build)}</Td>
                      <Td>{v.users}</Td>
                      <Td>{v.share}%</Td>
                      <Td style={{ color: MUTED, fontSize: 12, whiteSpace: 'nowrap' }}>{dayjs(v.lastSeen).format("DD/MM/YYYY HH:mm")}</Td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <SectionTitle>
              {strings.LABEL_VERSIONS_BEHIND} <span style={{ color: MUTED, fontWeight: 400 }}>({stats.behind.length})</span>
            </SectionTitle>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr>
                    <ColHeader>{strings.LABEL_RATING_CUSTOMER}</ColHeader>
                    <ColHeader>{strings.LABEL_RATING_ACCOUNT}</ColHeader>
                    <ColHeader>{strings.LABEL_VERSIONS_VERSION}</ColHeader>
                    <ColHeader>{strings.LABEL_VERSIONS_LAST_SEEN}</ColHeader>
                    <ColHeader>{strings.LABEL_RATING_SHOPS}</ColHeader>
                    <ColHeader>{strings.LABEL_RATING_CONTACT}</ColHeader>
                  </tr>
                </thead>
                <tbody>
                  {stats.behind.length === 0 && (
                    <tr><td colSpan={6} style={{ textAlign: 'center', padding: 30, fontSize: 13, color: MUTED }}>{strings.LABEL_VERSIONS_ALL_UP_TO_DATE}</td></tr>
                  )}
                  {stats.behind.map((u) => (
                    <tr key={u.id}>
                      <Td style={{ color: TEXT }}>{u.name || '—'}</Td>
                      <Td style={{ fontSize: 12, whiteSpace: 'nowrap' }}>{TYPE_LABELS[u.type] || u.type || '—'}</Td>
                      <Td style={{ fontSize: 12, whiteSpace: 'nowrap' }}>
                        {PLATFORM_LABELS[u.platform] || u.platform} · {versionLabel(u.version, u.build)}
                      </Td>
                      <Td style={{ color: MUTED, fontSize: 12, whiteSpace: 'nowrap' }}>{dayjs(u.lastSeen).format("DD/MM/YYYY")}</Td>
                      <Td style={{ fontSize: 12 }}>{u.shops.length ? u.shops.join(', ') : <span style={{ color: MUTED }}>—</span>}</Td>
                      <Td style={{ fontSize: 12, maxWidth: 260 }}><Contact user={u} /></Td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>
    </Box>
  );
};

export default VersionList;
